<script setup lang="ts">
import MediumModel from '@/components/Interface/Modal/ModelSizes/MediumModel.vue'
import ActionButton from '@/components/Elements/Buttons/ActionButton.vue'
import { useBattleSimulatorStore } from '@/stores/battleSimulatorStore'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const battleSimulatorStore = useBattleSimulatorStore()

const result = computed(() => battleSimulatorStore.lastBattleResult)

const rerunBattle = () => {
  battleSimulatorStore.runBattle()
}

const winnerText = computed(() => {
  if (!result.value) return ''
  switch (result.value.winner) {
    case 'attacker': return t('modals.battleSimulatorResult.attackerWins')
    case 'defender': return t('modals.battleSimulatorResult.defenderWins')
    case 'draw': return t('modals.battleSimulatorResult.draw')
    default: return ''
  }
})

const winnerClass = computed(() => {
  if (!result.value) return ''
  switch (result.value.winner) {
    case 'attacker': return 'text-green-400'
    case 'defender': return 'text-red-400'
    case 'draw': return 'text-yellow-400'
    default: return ''
  }
})
</script>

<template>
  <MediumModel :title="t('modals.battleSimulatorResult.title')">
    <div v-if="result" class="p-4">
      <!-- Winner Banner -->
      <div class="text-center mb-6">
        <div :class="['text-3xl font-bold', winnerClass]">{{ winnerText }}</div>
        <div class="text-metal-400 text-sm mt-1">{{ t('modals.battleSimulatorResult.rounds', { count: result.rounds }) }}</div>
      </div>

      <!-- Stats Grid -->
      <div class="grid grid-cols-3 gap-4 text-center">
        <!-- Attacker Column -->
        <div class="bg-metal-800/60 rounded p-3">
          <div class="text-metal-400 text-sm mb-2">{{ t('modals.battleSimulatorResult.attacker') }}</div>
          <div class="text-2xl font-bold text-metal-200">
            {{ result.attackerShipsEnd }}<span class="text-metal-500">/{{ result.attackerShipsStart }}</span>
          </div>
          <div class="text-metal-400 text-xs">{{ t('modals.battleSimulatorResult.shipsRemaining') }}</div>
        </div>

        <!-- VS -->
        <div class="flex items-center justify-center">
          <div class="text-metal-500 text-xl font-bold">{{ t('modals.battleSimulatorResult.vs') }}</div>
        </div>

        <!-- Defender Column -->
        <div class="bg-metal-800/60 rounded p-3">
          <div class="text-metal-400 text-sm mb-2">{{ t('modals.battleSimulatorResult.defender') }}</div>
          <div class="text-2xl font-bold text-metal-200">
            {{ result.defenderShipsEnd }}<span class="text-metal-500">/{{ result.defenderShipsStart }}</span>
          </div>
          <div class="text-metal-400 text-xs">{{ t('modals.battleSimulatorResult.shipsRemaining') }}</div>
        </div>
      </div>

      <!-- Planet Stats (if applicable) -->
      <div v-if="result.planetTroopsStart > 0 || result.planetShieldsStart > 0" class="mt-4">
        <div class="text-metal-400 text-sm mb-2 text-center">{{ t('modals.battleSimulatorResult.planetDefense') }}</div>
        <div class="grid grid-cols-2 gap-4">
          <!-- Shields -->
          <div class="bg-metal-800/60 rounded p-3">
            <div class="text-metal-400 text-xs mb-1">{{ t('modals.battleSimulatorResult.shields') }}</div>
            <div class="h-2 bg-metal-700 rounded overflow-hidden">
              <div
                class="h-full bg-blue-500 transition-all"
                :style="{ width: result.planetShieldsEnd + '%' }"
              ></div>
            </div>
            <div class="text-metal-300 text-sm mt-1">
              {{ result.planetShieldsEnd }}%
              <span class="text-metal-500">({{ t('modals.battleSimulatorResult.was', { value: result.planetShieldsStart }) }})</span>
            </div>
          </div>

          <!-- Troops -->
          <div class="bg-metal-800/60 rounded p-3">
            <div class="text-metal-400 text-xs mb-1">{{ t('modals.battleSimulatorResult.troops') }}</div>
            <div class="h-2 bg-metal-700 rounded overflow-hidden">
              <div
                class="h-full bg-red-500 transition-all"
                :style="{ width: result.planetTroopsEnd + '%' }"
              ></div>
            </div>
            <div class="text-metal-300 text-sm mt-1">
              {{ result.planetTroopsEnd }}%
              <span class="text-metal-500">({{ t('modals.battleSimulatorResult.was', { value: result.planetTroopsStart }) }})</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Battle Summary -->
      <div class="mt-4 bg-metal-800/40 rounded p-3">
        <div class="text-metal-400 text-sm mb-2">{{ t('modals.battleSimulatorResult.summary') }}</div>
        <div class="grid grid-cols-2 gap-2 text-sm">
          <div class="text-metal-400">{{ t('modals.battleSimulatorResult.attackerLosses') }}</div>
          <div class="text-metal-200">{{ t('modals.battleSimulatorResult.ships', { count: result.attackerShipsStart - result.attackerShipsEnd }) }}</div>
          <div class="text-metal-400">{{ t('modals.battleSimulatorResult.defenderLosses') }}</div>
          <div class="text-metal-200">{{ t('modals.battleSimulatorResult.ships', { count: result.defenderShipsStart - result.defenderShipsEnd }) }}</div>
        </div>
      </div>

      <!-- Damage Stats -->
      <div class="mt-4 bg-metal-800/40 rounded p-3">
        <div class="text-metal-400 text-sm mb-2">{{ t('modals.battleSimulatorResult.damageDealt') }}</div>
        <div class="grid grid-cols-2 gap-4">
          <!-- Attacker Damage -->
          <div>
            <div class="text-metal-500 text-xs mb-1">{{ t('modals.battleSimulatorResult.attackerDealt') }}</div>
            <div class="text-sm">
              <div class="flex justify-between">
                <span class="text-metal-400">{{ t('modals.battleSimulatorResult.vsFleet') }}</span>
                <span class="text-green-400">{{ result.defenderDamageTakenFleetVsFleet.toLocaleString() }}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-metal-400">{{ t('modals.battleSimulatorResult.vsPlanet') }}</span>
                <span class="text-green-400">{{ result.defenderDamageTakenFleetVsPlanet.toLocaleString() }}</span>
              </div>
            </div>
          </div>
          <!-- Defender Damage -->
          <div>
            <div class="text-metal-500 text-xs mb-1">{{ t('modals.battleSimulatorResult.defenderDealt') }}</div>
            <div class="text-sm">
              <div class="flex justify-between">
                <span class="text-metal-400">{{ t('modals.battleSimulatorResult.fleetVsFleet') }}</span>
                <span class="text-red-400">{{ result.attackerDamageTakenFleetVsFleet.toLocaleString() }}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-metal-400">{{ t('modals.battleSimulatorResult.planetVsFleet') }}</span>
                <span class="text-red-400">{{ result.attackerDamageTakenPlanetVsFleet.toLocaleString() }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Rerun Button -->
      <div class="mt-4 flex justify-center">
        <ActionButton @click="rerunBattle">{{ t('command.battleSimulator.rerunBattle') }}</ActionButton>
      </div>
    </div>
    <div v-else class="p-4 text-center text-metal-400">
      {{ t('modals.battleSimulatorResult.noResults') }}
    </div>
  </MediumModel>
</template>

<style scoped lang="scss"></style>
